import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { alert } from "../../../../Utils/toast";
import { getUserChatsActions } from "../../ai/getUserChats/getUserChats";
import { GetUserChatsDataTypes } from "../../../../Schema/getUserChats.types";
import {
  resetStreamingChatRes,
  setPreventToGetChats,
  setResetAllStreamed,
} from "../../streamedChat/streamedChat";

import { resetFetchedChats } from "../../subService/subServiceChats";
import { queryClient } from "../../../store";

export type AgencyCreateUserServiceTypes = {
  incognito?: number;
  sub_service_id: number;
  openNewSession?: boolean;
};

export type AgencyCreateUserServiceLoadAction = {
  type: string;
  payload: AgencyCreateUserServiceTypes;
};

export type AgencyCreateUserServiceData = {
  data: {
    user_sub_service: {
      id: string | number;
      unique_token: string;
    };
    data: GetUserChatsDataTypes;
    message_count: number;
    message_limit: number;
  };
};

const AgencyCreateUserService = new ReduxFetchState<
  AgencyCreateUserServiceData | null,
  AgencyCreateUserServiceTypes,
  any
>("agencyCreateUserService");

export function* watchAgencyCreateUserService(action: AgencyCreateUserServiceLoadAction) {
  try {
    const { incognito, sub_service_id, openNewSession } = action.payload;
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const data: AgencyCreateUserServiceData = yield axios({
      url: `api/v1/ai/agency/user-sub-service/create`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
        lang,
      },
      data: {
        incognito,
        sub_service_id,
      },
    });

    yield put(setResetAllStreamed());
    yield put(resetStreamingChatRes());

    if (openNewSession) {
      yield put(setPreventToGetChats(true));
      yield put(resetFetchedChats());
    }

    yield put(AgencyCreateUserService.actions.loadSuccess({ data: data.data }));
    queryClient.removeQueries({ queryKey: ["getUserHistory", lang] });

    yield put(getUserChatsActions.loadSuccess(null));
  } catch (e: any) {
    alert({
      message: e?.response?.data?.error ?? "مشکلی پیش آمده",
      type: "error",
    });
    yield put(AgencyCreateUserService.actions.loadFailure(e.message));
  }
}

export function* agencyCreateUserServiceSagas() {
  yield takeEvery(AgencyCreateUserService.actionTypes.load, watchAgencyCreateUserService);
}

export function useAgencyCreateUserService() {
  const { loading, data, ...agencyCreateUserServiceState } = useAppSelector(
    (state) => state.agencyCreateUserService,
  );
  const dispatch = useAppDispatch();

  const dispatchAgencyCreateUserService = useCallback(
    (data: AgencyCreateUserServiceTypes) => {
      dispatch(AgencyCreateUserService.actions.load(data));
    },
    [dispatch],
  );

  return {
    ...agencyCreateUserServiceState,
    dispatchAgencyCreateUserService,
    agencyCreateUserServiceLoading: loading,
    agencyCreateUserServiceData: data,
  };
}

export const {
  actionTypes: agencyCreateUserServiceActionsTypes,
  actions: agencyCreateUserServiceActions,
  reducer: agencyCreateUserServiceReducer,
} = AgencyCreateUserService;